import {
  Address,
  Badge,
  Button,
  buttonClasses,
  toast,
  Card,
  CardTitle,
  EmptyState,
  Modal,
  Notice,
  Segmented,
  Skeleton,
  formatDateTime,
  Spot,
} from "@custos/ui";
import { AUTO_LOCK_CHOICES_MINUTES, type AutoLockMinutes } from "@custos/vault";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { LogOut, Plus, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { AddAddressModal } from "../components/AddAddressModal";
import { PageHeader, useLogout } from "../components/AppShell";
import { api } from "../lib/api";
import { describeDevice } from "../lib/devices";
import { errorMessage } from "../lib/errors";
import { usePrefs, type DisplayCurrency, type ThemePref } from "../lib/prefs";
import { qk, useMe, useSessions, useWhitelist } from "../lib/queries";

function Section({
  title,
  description,
  children,
  id,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  id: string;
}) {
  return (
    <Card aria-labelledby={id}>
      <CardTitle id={id}>{title}</CardTitle>
      {description && <p className="mt-1 text-sm text-muted">{description}</p>}
      <div className="mt-4">{children}</div>
    </Card>
  );
}

export function SettingsPage() {
  const theme = usePrefs((s) => s.theme);
  const setTheme = usePrefs((s) => s.setTheme);
  const currency = usePrefs((s) => s.currency);
  const setCurrency = usePrefs((s) => s.setCurrency);
  const autoLock = usePrefs((s) => s.vaultAutoLockMinutes);
  const setAutoLock = usePrefs((s) => s.setVaultAutoLockMinutes);
  const logout = useLogout();
  const me = useMe();

  return (
    <>
      <PageHeader title="Settings" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section id="appearance" title="Appearance">
          <Segmented<ThemePref>
            label="Theme"
            value={theme}
            onChange={setTheme}
            options={[
              { value: "dark", label: "Dark" },
              { value: "light", label: "Light" },
              { value: "system", label: "System" },
            ]}
          />
        </Section>

        <Section
          id="currency"
          title="Display currency"
          description="Used for your total, asset values and the chart (USD, SGD or GBP)."
        >
          <Segmented<DisplayCurrency>
            label="Display currency"
            value={currency}
            onChange={setCurrency}
            options={[
              { value: "USD", label: "USD" },
              { value: "SGD", label: "SGD" },
              { value: "GBP", label: "GBP" },
            ]}
          />
        </Section>

        <Section
          id="vault-lock"
          title="Vault auto-lock"
          description="The vault locks itself after this long without activity (and always when you reload, close the tab or log out)."
        >
          <Segmented
            label="Vault auto-lock"
            value={String(autoLock)}
            onChange={(v) => setAutoLock(Number(v) as AutoLockMinutes)}
            options={AUTO_LOCK_CHOICES_MINUTES.map((m) => ({ value: String(m), label: `${m} min` }))}
          />
        </Section>

        <Section
          id="help"
          title="Help"
          description="Questions about your account, a deposit or a withdrawal?"
        >
          <Link to="/help" className={buttonClasses("secondary", "sm")}>
            Chat with support
          </Link>
        </Section>

        <Section id="security" title="Security">
          {me.data ? (
            <div className="flex flex-col gap-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-muted">Email</span>
                <span className="font-medium break-all text-fg" data-testid="account-email">
                  {me.data.email}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-muted">Two-factor authentication</span>
                <Badge tone={me.data.totpEnabled ? "success" : "warning"}>
                  {me.data.totpEnabled ? "On" : "Off"}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-muted">Password</span>
                <Link to="/forgot-password" className="font-medium text-primary-strong hover:underline">
                  Change via email link
                </Link>
              </div>
            </div>
          ) : (
            <Skeleton className="h-20" />
          )}
        </Section>

        <SessionsSection />
        <div className="lg:col-span-2">
          <WhitelistSection />
        </div>
      </div>
      <Button variant="secondary" className="mt-6 w-full sm:w-auto" onClick={logout}>
        <LogOut className="size-4" aria-hidden /> Log out
      </Button>
    </>
  );
}

function SessionsSection() {
  const sessions = useSessions();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.sessions });
  const revokeOthers = useMutation({ mutationFn: () => api.revokeOtherSessions(), onSuccess: refresh });
  const revokeOne = useMutation({ mutationFn: (id: string) => api.revokeSession(id), onSuccess: refresh });
  const others = (sessions.data ?? []).filter((s) => !s.current).length;

  return (
    <Section id="sessions" title="Sessions" description="Devices where you're logged in.">
      {sessions.isPending ? (
        <Skeleton className="h-24" />
      ) : sessions.error ? (
        <Notice tone="danger">{errorMessage(sessions.error)}</Notice>
      ) : (
        <ul className="flex flex-col divide-y divide-line" data-testid="sessions">
          {sessions.data.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between gap-3 py-2.5"
              data-testid="session-row"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg">
                  {describeDevice(s.userAgent)}
                  {s.current && <Badge tone="success">This device</Badge>}
                </p>
                <p className="text-xs text-muted">
                  {s.ip ?? "Unknown IP"} · last active {formatDateTime(s.lastUsedAt)}
                </p>
              </div>
              {!s.current && (
                <Button
                  variant="ghost"
                  size="sm"
                  loading={revokeOne.isPending && revokeOne.variables === s.id}
                  onClick={() => revokeOne.mutate(s.id)}
                >
                  Log out
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {(revokeOthers.error || revokeOne.error) && (
        <Notice tone="danger">{errorMessage(revokeOthers.error ?? revokeOne.error)}</Notice>
      )}
      {revokeOthers.isSuccess && (
        <Notice tone="success" className="mt-2">
          Logged out {revokeOthers.data.revoked} other device{revokeOthers.data.revoked === 1 ? "" : "s"}.
        </Notice>
      )}
      <Button
        variant="secondary"
        className="mt-3"
        disabled={others === 0}
        loading={revokeOthers.isPending}
        onClick={() => revokeOthers.mutate()}
      >
        Log out other devices
      </Button>
    </Section>
  );
}

function WhitelistSection() {
  const whitelist = useWhitelist();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.removeWhitelist(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.whitelist });
      setRemoveId(null);
      toast("Address removed");
    },
  });
  const toRemove = whitelist.data?.find((w) => w.id === removeId);

  return (
    <Section
      id="whitelist"
      title="Withdrawal addresses"
      description="Withdrawals can only go to these saved addresses."
    >
      {whitelist.isPending ? (
        <Skeleton className="h-20" />
      ) : whitelist.error ? (
        <Notice tone="danger">{errorMessage(whitelist.error)}</Notice>
      ) : whitelist.data.length === 0 ? (
        <EmptyState icon={<Spot name="addresses" />} title="No saved addresses" />
      ) : (
        <ul className="flex flex-col divide-y divide-line" data-testid="whitelist">
          {whitelist.data.map((w) => (
            <li
              key={w.id}
              className="flex items-center justify-between gap-3 py-2.5"
              data-testid="whitelist-row"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg">
                  {w.label || "Saved address"}
                  <Badge>{w.network}</Badge>
                  {!w.usable && <Badge tone="warning">Usable from {formatDateTime(w.activeFrom)}</Badge>}
                </p>
                <Address value={w.address} start={8} end={6} />
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${w.label || w.address}`}
                onClick={() => setRemoveId(w.id)}
              >
                <Trash2 className="size-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button variant="secondary" className="mt-3" onClick={() => setAddOpen(true)}>
        <Plus className="size-4" aria-hidden /> Add address
      </Button>
      <AddAddressModal open={addOpen} onOpenChange={setAddOpen} />
      <Modal
        open={removeId !== null}
        onOpenChange={(open) => {
          if (!open) {
            remove.reset();
            setRemoveId(null);
          }
        }}
        title="Remove this address?"
        description="Adding it again later starts a new waiting period."
      >
        <div className="flex flex-col gap-3">
          {toRemove && <p className="font-mono text-xs break-all text-fg">{toRemove.address}</p>}
          {remove.error && <Notice tone="danger">{errorMessage(remove.error)}</Notice>}
          <Button
            variant="danger"
            block
            loading={remove.isPending}
            onClick={() => removeId && remove.mutate(removeId)}
          >
            Remove address
          </Button>
        </div>
      </Modal>
    </Section>
  );
}
